// Single source of truth for application URLs: the router consumes each
// entry's `pattern`, and every internal link, redirect, and navigate call
// builds its path through `build`, so a URL is defined exactly once. Params
// are required strings; callers resolve missing identifiers before building.
type ProjectParams = { project: string };
type ProjectIdParams = ProjectParams & { id: string };
type SourceParams = ProjectParams & { sourceId: string };
type ConversationParams = ProjectParams & { threadId: string };
type CustomizationParams = ProjectParams & { customizationId: string };

const encode = (value: string) => encodeURIComponent(value);

export const routes = {
  getStarted: {
    pattern: '/:project/get-started',
    build: ({ project }: ProjectParams) => `/${encode(project)}/get-started`,
  },
  dashboard: {
    pattern: '/:project',
    build: ({ project }: ProjectParams) => `/${encode(project)}`,
  },
  conversations: {
    pattern: '/:project/conversations',
    build: ({ project }: ProjectParams) => `/${encode(project)}/conversations`,
  },
  conversation: {
    pattern: '/:project/conversations/:threadId',
    build: ({ project, threadId }: ConversationParams) =>
      `/${encode(project)}/conversations/${encode(threadId)}`,
  },
  coverageGaps: {
    pattern: '/:project/coverage-gaps',
    build: ({ project }: ProjectParams) => `/${encode(project)}/coverage-gaps`,
  },
  coverageGapClusterSet: {
    pattern: '/:project/coverage-gaps/:id',
    build: ({ project, id }: ProjectIdParams) =>
      `/${encode(project)}/coverage-gaps/${encode(id)}`,
  },
  coverageGapCluster: {
    pattern: '/:project/coverage-gaps/clusters/:id',
    build: ({ project, id }: ProjectIdParams) =>
      `/${encode(project)}/coverage-gaps/clusters/${encode(id)}`,
  },
  sources: {
    pattern: '/:project/sources',
    build: ({ project }: ProjectParams) => `/${encode(project)}/sources`,
  },
  sourceNew: {
    pattern: '/:project/sources/new',
    build: ({ project }: ProjectParams) => `/${encode(project)}/sources/new`,
  },
  sourceNewWebsite: {
    pattern: '/:project/sources/new/website',
    build: ({ project }: ProjectParams) =>
      `/${encode(project)}/sources/new/website`,
  },
  source: {
    pattern: '/:project/sources/:sourceId',
    build: ({ project, sourceId }: SourceParams) =>
      `/${encode(project)}/sources/${encode(sourceId)}`,
  },
  /** Legacy: renders a redirect to `source`. Do not build links to it. */
  sourceReview: {
    pattern: '/:project/sources/review/:sourceId',
    build: ({ project, sourceId }: SourceParams) =>
      `/${encode(project)}/sources/review/${encode(sourceId)}`,
  },
  /** Legacy: renders a redirect to `source`. Do not build links to it. */
  sourceView: {
    pattern: '/:project/sources/view/:sourceId',
    build: ({ project, sourceId }: SourceParams) =>
      `/${encode(project)}/sources/view/${encode(sourceId)}`,
  },
  integrations: {
    pattern: '/:project/integrations',
    build: ({ project }: ProjectParams) => `/${encode(project)}/integrations`,
  },
  integrationNew: {
    pattern: '/:project/integrations/new',
    build: ({ project }: ProjectParams) =>
      `/${encode(project)}/integrations/new`,
  },
  integration: {
    pattern: '/:project/integrations/:id',
    build: ({ project, id }: ProjectIdParams) =>
      `/${encode(project)}/integrations/${encode(id)}`,
  },
  customizations: {
    pattern: '/:project/customizations',
    build: ({ project }: ProjectParams) => `/${encode(project)}/customizations`,
  },
  customization: {
    pattern: '/:project/customizations/:customizationId',
    build: ({ project, customizationId }: CustomizationParams) =>
      `/${encode(project)}/customizations/${encode(customizationId)}`,
  },
  sourceAnalytics: {
    pattern: '/:project/source-analytics',
    build: ({ project }: ProjectParams) =>
      `/${encode(project)}/source-analytics`,
  },
  sourceGroups: {
    pattern: '/:project/source-groups',
    build: ({ project }: ProjectParams) => `/${encode(project)}/source-groups`,
  },
  tags: {
    pattern: '/:project/tags',
    build: ({ project }: ProjectParams) => `/${encode(project)}/tags`,
  },
  users: {
    pattern: '/:project/users',
    build: ({ project }: ProjectParams) => `/${encode(project)}/users`,
  },
  apiKeys: {
    pattern: '/:project/api-keys',
    build: ({ project }: ProjectParams) => `/${encode(project)}/api-keys`,
  },
  playground: {
    pattern: '/:project/playground',
    build: ({ project }: ProjectParams) => `/${encode(project)}/playground`,
  },
  topQuestions: {
    pattern: '/:project/top-questions',
    build: ({ project }: ProjectParams) => `/${encode(project)}/top-questions`,
  },
  topQuestionClusterSet: {
    pattern: '/:project/top-questions/:id',
    build: ({ project, id }: ProjectIdParams) =>
      `/${encode(project)}/top-questions/${encode(id)}`,
  },
  topQuestionCluster: {
    pattern: '/:project/top-questions/clusters/:id',
    build: ({ project, id }: ProjectIdParams) =>
      `/${encode(project)}/top-questions/clusters/${encode(id)}`,
  },
  onboarding: {
    pattern: '/onboarding',
    build: () => '/onboarding',
  },
  settings: {
    pattern: '/settings',
    build: () => '/settings',
  },
  settingsAccount: {
    pattern: '/settings/account',
    build: () => '/settings/account',
  },
  settingsAuth: {
    pattern: '/settings/auth',
    build: () => '/settings/auth',
  },
  settingsProjects: {
    pattern: '/settings/projects',
    build: () => '/settings/projects',
  },
  settingsTeam: {
    pattern: '/settings/team',
    build: () => '/settings/team',
  },
  settingsUsage: {
    pattern: '/settings/usage',
    build: () => '/settings/usage',
  },
} as const;

type RouteKey = keyof typeof routes;
export type SettingsRouteKey = Extract<RouteKey, `settings${string}`>;

export const withSearchParams = (
  path: string,
  params: Record<string, string | number | boolean | undefined>,
) => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) searchParams.set(key, String(value));
  }
  const search = searchParams.toString();
  if (!search) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${search}`;
};
